import { Global, css } from '@emotion/react';
import cx from 'classnames';
import React, { forwardRef, useState } from 'react';
import type { FieldInputProps } from 'react-final-form';
import Input, { Country as CountryCode } from 'react-phone-number-input';

import FieldContainer from '@skippay/core/form-controls-new/FieldContainer';
import classes from '@skippay/core/form-controls-new/FieldContainer/index.module.sass';
import { useIntl } from '@skippay/core/i18n/intl';
import { useLocaleContext } from '@skippay/core/providers/LocaleProvider/LocaleProvider';
import { size } from '@skippay/core/styles/spacing';
import { color } from '@skippay/core/styles/variables';
import 'react-phone-number-input/style.css';

const globalStyles = css`
	body {
		--PhoneInputCountrySelect-marginRight: ${size(4)};
		--PhoneInputCountrySelectArrow-opacity: 1;
		--PhoneInputCountrySelectArrow-transform: none;
	}

	.PhoneInputCountrySelectArrow {
		width: 0;
		height: 0;
		border-style: solid;
		border-width: 6px 5px 0 5px;
		border-color: ${color('gray-900')} transparent transparent transparent;
		margin-left: ${size(4)};

		.dark & {
			border-color: ${color('white')} transparent transparent transparent;
		}
	}

	.PhoneInputInput {
		height: 30px;
		padding: 2px 0;

		&::placeholder {
			color: ${color('light-gray-500')};
		}

		.PhoneInput--readOnly & {
			color: ${color('light-gray-300')};
		}
	}

	.PhoneInput--readOnly {
		.PhoneInputCountry {
			opacity: 0.5;
		}
	}
`;

const PHONE_INPUT_ID = 'phoneInput';

const PhoneInput: React.FC<Props> = forwardRef(
	({ id, animatedLabel, bold, className, value, placeholder, onFocus, onBlur, ...other }) => {
		const intl = useIntl();
		const [isSelected, setIsSelected] = useState(false);

		const { locale } = useLocaleContext();
		const defaultCountryCode: CountryCode = locale === 'cs' ? 'CZ' : 'SK';

		return (
			<>
				<Global styles={globalStyles} />
				<FieldContainer className={className} bold={bold}>
					<Input
						onFocus={() => {
							setIsSelected(true);
							onFocus?.();
						}}
						onBlur={() => {
							setIsSelected(false);
							onBlur?.();
						}}
						countries={['CZ', 'SK']}
						labels={{
							CZ: intl.formatMessage({
								defaultMessage: 'Česká republika +420',
								id: `PhoneInput.cz`,
							}),
							SK: intl.formatMessage({
								defaultMessage: 'Slovenská republika +421',
								id: `PhoneInput.sk`,
							}),
						}}
						defaultCountry={defaultCountryCode}
						addInternationalOption={false}
						limitMaxLength
						value={value}
						placeholder={animatedLabel ? '' : placeholder}
						{...other}
						id={id || PHONE_INPUT_ID}
						data-clarity-mask="true"
					/>

					{animatedLabel && (
						<label
							htmlFor={id || PHONE_INPUT_ID}
							className={cx(
								classes.animatedLabel,
								classes.animatedLabelPhone,
								(isSelected || value) && classes.animatedLabelActive
							)}
						>
							<span>{animatedLabel}</span>
						</label>
					)}
				</FieldContainer>
			</>
		);
	}
);

export type Props = Partial<FieldInputProps<string, HTMLInputElement>> & {
	animatedLabel?: React.ReactNode;
	'aria-describedby'?: string;
	'aria-label': string;
	'aria-labeledby'?: string;
	readOnly?: boolean;
	className?: string;
	defaultValue?: string | number;
	id?: string;
	inputClassName?: string;
	bold?: boolean;
};

export default PhoneInput;
